<template>
  <div class="notification-bell" ref="bellRef">
    <button
      type="button"
      class="notification-bell__btn"
      :class="{ 'notification-bell__btn--active': isOpen }"
      :aria-label="t('notifications.bellLabel')"
      @click.stop="toggleDropdown"
    >
      <span class="material-symbols-outlined">notifications</span>
      <span v-if="unreadCount > 0" class="notification-bell__badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>
    <NotificationDropdown
      v-if="isOpen"
      :notifications="notifications"
      :loading="loading"
      :loading-more="loadingMore"
      :has-more="hasMore"
      :pending-new-count="pendingNewCount"
      :active-tab="activeTab"
      :billing-unread="billingUnread"
      :system-unread="systemUnread"
      :promo-unread="promoUnread"
      :show-billing-tab="showBillingTab"
      :show-system-tab="showSystemTab"
      :show-promo-tab="showPromoTab"
      :search="searchQuery"
      :filter-type="filterType"
      :type-options="typeOptions"
      :filters-active="filtersActive"
      @close="closeDropdown"
      @markRead="handleMarkRead"
      @markAllRead="handleMarkAllRead"
      @openNotification="handleOpenNotification"
      @switchTab="handleSwitchTab"
      @goSettings="handleGoSettings"
      @loadMore="handleLoadMore"
      @refresh="handleRefresh"
      @update:search="handleSearchInput"
      @update:filterType="handleFilterType"
      @resetFilters="handleResetFilters"
    />
    <NotificationModal
      :open="isModalOpen"
      :notification="selectedNotification"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { t } from '../services/i18nService'
import { hasPermission, isSubUser } from '../services/authService'
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../services/billingService'
import NotificationDropdown from './NotificationDropdown.vue'
import NotificationModal from './NotificationModal.vue'
import { useModules } from '../composables/useModules'
// Модулі порталу: сповіщення вимкнених модулів (dtc, tacho, task_edited) у дзвіночок не потрапляють
const { isInAppTypeVisible } = useModules()

const props = defineProps({
  showBillingTab: { type: Boolean, default: true },
  showSystemTab: { type: Boolean, default: true },
})

// Кампанию дилера открывает App.vue своим окном (картинка, кнопка «Хочу»), не NotificationModal
const emit = defineEmits(['openCampaign'])

const router = useRouter()
const bellRef = ref(null)
const isOpen = ref(false)
const billingUnread = ref(0)
const systemUnread = ref(0)
const promoUnread = ref(0)
// Сколько кампаний дилера пришло всего: вкладку «Пропозиції» без кампаний не показываем
const promoTotal = ref(0)
// Пропозиції только владельцу аккаунта, сотрудникам клиента сервер их не отдаёт
const promoAllowed = !isSubUser()
const showPromoTab = computed(() => promoAllowed && promoTotal.value > 0)
const notifications = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const isModalOpen = ref(false)
const selectedNotification = ref(null)

// Розмір сторінки для дропдауна
const PAGE_SIZE = 20

// Типы событий в фильтре по вкладкам (порядок = порядок в списке)
const FEED_TYPES = {
  system: ['geozone_enter', 'geozone_exit', 'tracker_loss', 'tracker_online', 'sensor_trigger',
    'alarm_trigger', 'fuel_refuel', 'fuel_drain', 'station_intake', 'station_card', 'dtc', 'tacho', 'generator',
    'sent_etoll_no_fix', 'task_edited'],
  billing: ['invoice_issued', 'invoice_cancelled', 'payment_reminder', 'payment_overdue', 'warning',
    'suspension_notice', 'block_notice', 'unblock_notice', 'custom'],
  promo: [],
}
const FUEL_TYPES = ['fuel_refuel', 'fuel_drain']
// Приходы и нарушения карт заправки — за отдельным правом «АЗС»
const STATION_TYPES = ['station_card', 'station_intake']
// Право субпользователя «Паливо» (владелец — всегда): без него заправки и сливы сервер не отдаёт
const fuelVisible = hasPermission('canSeeFuel')
const stationVisible = hasPermission('canSeeStation')

// Задержка поиска: запрос уходит, когда пользователь перестал печатать
const SEARCH_DEBOUNCE_MS = 350

// Знімок API unread на момент завантаження списку — для обчислення «нових з моменту відкриття»
const unreadSnapshot = ref({ billing: 0, system: 0, promo: 0 })

// Счётчик непрочитанных по вкладке
const unreadRefs = { billing: billingUnread, system: systemUnread, promo: promoUnread }
const tabOf = (category) => (unreadRefs[category] ? category : 'system')

// Перша доступна вкладка за замовчуванням
const activeTab = ref(props.showBillingTab ? 'billing' : 'system')

// Фильтр ленты: что в поле ввода и что реально ушло в запрос (ввод догоняет запрос с задержкой)
const searchQuery = ref('')
const appliedSearch = ref('')
const filterType = ref('')
let searchTimer = null
// Номер запроса ленты: ответ устаревшего запроса (быстрый ввод, смена вкладки) отбрасываем
let feedRequestSeq = 0

// Типы для выпадающего списка: без выключенных модулей и без топлива, если оно скрыто
const typeOptions = computed(() =>
  (FEED_TYPES[activeTab.value] || []).filter(
    (type) => isInAppTypeVisible(type) && (fuelVisible || !FUEL_TYPES.includes(type))
      && (stationVisible || !STATION_TYPES.includes(type))
  )
)

const filtersActive = computed(() => !!filterType.value || !!appliedSearch.value)

const feedFilters = () => ({
  types: filterType.value ? [filterType.value] : [],
  q: appliedSearch.value,
})

// Кількість нових сповіщень з моменту останнього завантаження активної вкладки
const pendingNewCount = computed(() => {
  const current = unreadRefs[activeTab.value]?.value || 0
  const snapshot = unreadSnapshot.value[activeTab.value] || 0
  return Math.max(0, current - snapshot)
})

// Загальна кількість непрочитаних — сума доступних категорій
const unreadCount = computed(() => {
  let count = 0
  if (props.showBillingTab) count += billingUnread.value
  if (props.showSystemTab) count += systemUnread.value
  if (promoAllowed) count += promoUnread.value
  return count
})

const loadUnreadCount = async () => {
  try {
    const response = await fetchUnreadCount()
    billingUnread.value = response?.billing || 0
    systemUnread.value = response?.system || 0
    promoUnread.value = response?.promo || 0
    promoTotal.value = response?.promoTotal || 0
  } catch {
    billingUnread.value = 0
    systemUnread.value = 0
    promoUnread.value = 0
  }
}

const loadNotifications = async () => {
  const seq = ++feedRequestSeq
  loading.value = true
  try {
    // Синхронізуємо лічильники з API одночасно зі списком, щоб знімок був актуальним
    const [, response] = await Promise.all([
      loadUnreadCount(),
      fetchNotifications(PAGE_SIZE, 0, false, activeTab.value, feedFilters()),
    ])
    if (seq !== feedRequestSeq) return
    const list = response?.notifications || []
    // Уведомления выключенных модулей (dtc, tacho, task_edited) сервер уже не отдаёт; страховка по реестру
    notifications.value = list.filter((n) => isInAppTypeVisible(n?.type))
    hasMore.value = list.length === PAGE_SIZE
    // Фіксуємо знімок непрочитаних — щоб порахувати «скільки нових прийшло після відкриття»
    unreadSnapshot.value = {
      billing: billingUnread.value,
      system: systemUnread.value,
      promo: promoUnread.value,
    }
  } catch {
    if (seq !== feedRequestSeq) return
    notifications.value = []
    hasMore.value = false
  } finally {
    if (seq === feedRequestSeq) loading.value = false
  }
}

const loadMoreNotifications = async () => {
  if (loadingMore.value || !hasMore.value) return
  const seq = feedRequestSeq
  loadingMore.value = true
  try {
    const offset = notifications.value.length
    const response = await fetchNotifications(PAGE_SIZE, offset, false, activeTab.value, feedFilters())
    // Пока грузилась страница, сменился фильтр или вкладка — эта страница уже не к той ленте
    if (seq !== feedRequestSeq) return
    const list = response?.notifications || []
    // Захист від дублювання id (якщо нові події прийшли між сторінками)
    const knownIds = new Set(notifications.value.map((n) => n.id))
    const fresh = list.filter((n) => !knownIds.has(n.id) && isInAppTypeVisible(n?.type))
    notifications.value = [...notifications.value, ...fresh]
    hasMore.value = list.length === PAGE_SIZE
  } catch {
    if (seq === feedRequestSeq) hasMore.value = false
  } finally {
    loadingMore.value = false
  }
}

const handleLoadMore = () => {
  loadMoreNotifications()
}

const handleRefresh = () => {
  loadNotifications()
}

const resetFilters = () => {
  clearTimeout(searchTimer)
  searchQuery.value = ''
  appliedSearch.value = ''
  filterType.value = ''
}

// Одна буква находит почти всё — ищем с двух символов, короче считаем пустым поиском
const applySearch = () => {
  const q = searchQuery.value.trim()
  const next = q.length >= 2 ? q : ''
  if (next === appliedSearch.value) return
  appliedSearch.value = next
  loadNotifications()
}

const handleSearchInput = (value) => {
  searchQuery.value = value
  clearTimeout(searchTimer)
  // Очистку поля применяем сразу, без задержки
  if (!value) {
    applySearch()
    return
  }
  searchTimer = setTimeout(applySearch, SEARCH_DEBOUNCE_MS)
}

const handleFilterType = (type) => {
  filterType.value = type
  loadNotifications()
}

const handleResetFilters = () => {
  resetFilters()
  loadNotifications()
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    // Каждое открытие — с полной лентой: забытый фильтр выглядел бы как пропавшие уведомления
    resetFilters()
    loadNotifications()
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const handleSwitchTab = (tab) => {
  activeTab.value = tab
  // Типы у вкладок разные, поиск оставляем
  filterType.value = ''
  loadNotifications()
}

const handleMarkRead = async (id) => {
  try {
    await markNotificationRead(id)
    const notification = notifications.value.find((n) => n.id === id)
    if (notification && !notification.isRead) {
      notification.isRead = true
      // Зменшуємо лічильник відповідної категорії і знімок (щоб pendingNew залишався стабільним)
      const cat = tabOf(notification.category)
      unreadRefs[cat].value = Math.max(0, unreadRefs[cat].value - 1)
      unreadSnapshot.value[cat] = Math.max(0, unreadSnapshot.value[cat] - 1)
    }
  } catch {
    // Ігноруємо помилки
  }
}

const handleMarkAllRead = async () => {
  const filtered = filtersActive.value
  const tab = activeTab.value
  try {
    // При активном фильтре сервер отмечает только отфильтрованные уведомления
    await markAllNotificationsRead(tab, filtered ? feedFilters() : {})
    notifications.value.forEach((n) => (n.isRead = true))
    if (filtered) {
      // Сколько непрочитанных осталось вне фильтра, знает только сервер
      await loadUnreadCount()
      unreadSnapshot.value[tab] = unreadRefs[tab].value
    } else {
      // Обнуляємо лічильник активної вкладки і знімок
      unreadRefs[tab].value = 0
      unreadSnapshot.value[tab] = 0
    }
  } catch {
    // Ігноруємо помилки
  }
}

const handleOpenNotification = (notification) => {
  closeDropdown()
  // Кампания дилера: окно с картинкой и кнопкой держит App.vue (одно на оба колокольчика)
  if (notification?.type === 'campaign' && notification.referenceId) {
    emit('openCampaign', Number(notification.referenceId))
    return
  }
  selectedNotification.value = notification
  isModalOpen.value = true
}

// Перехід на сторінку налаштувань сповіщень
const handleGoSettings = () => {
  closeDropdown()
  router.push('/notifications')
}

const closeModal = () => {
  isModalOpen.value = false
  selectedNotification.value = null
}

const handleDocumentClick = (event) => {
  if (isOpen.value && bellRef.value && !bellRef.value.contains(event.target)) {
    closeDropdown()
  }
}

let pollInterval = null

// Оновлюємо ТІЛЬКИ лічильники. Список не перезавантажуємо, поки дропдаун відкритий —
// інакше скрол стрибає на початок прямо під час читання. Якщо прийшли нові,
// pendingNewCount > 0 і користувач сам тисне «оновити» в шапці списку.
const pollNotifications = async () => {
  await loadUnreadCount()
}

onMounted(() => {
  loadUnreadCount()
  document.addEventListener('click', handleDocumentClick)
  // Опитуємо уведомлення кожні 30 секунд
  pollInterval = setInterval(pollNotifications, 30000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  clearTimeout(searchTimer)
  if (pollInterval) {
    clearInterval(pollInterval)
  }
})
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.notification-bell__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
}

.notification-bell__btn:hover,
.notification-bell__btn--active {
  background: var(--bg-tertiary);
  color: var(--text-heading);
}

.notification-bell__btn .material-symbols-outlined {
  font-size: 24px;
}

.notification-bell__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--danger);
  color: var(--text-inverse);
  font-size: 0.65rem;
  font-weight: 700;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

</style>
